/**
 * Shared Permission Types
 *
 * Types shared between frontend and backend for permissions
 */

import { z } from 'zod/v4'

/**
 * Permission categories
 */
export type PermissionCategory =
  | 'transactions'
  | 'users'
  | 'reports'
  | 'imports'
  | 'settings'
  | 'billing'
  | 'audit'
  | 'admin'
  | 'data_retention'
  | 'teams'
  | 'api_keys'
  | 'webhooks'
  | 'exchanges'
  | 'sso'
  | 'exports'
  | 'assets'
  | 'templates'
  | 'system'
  | 'integrations'

/**
 * Built-in user roles
 */
export type BuiltInRole = 'super_admin' | 'org_admin' | 'user' | 'viewer'

/**
 * Permission entity
 */
export interface Permission {
  id: string
  key: string
  name: string
  description: string | null
  category: PermissionCategory
  isSystem: boolean
  createdAt: string
}

/**
 * Permission source
 */
export type PermissionSource = 'role' | 'custom_role' | 'team' | 'override'

/**
 * User permission detail
 */
export interface UserPermissionDetail {
  key: string
  name: string
  category: PermissionCategory
  granted: boolean
  source: PermissionSource
}

/**
 * User permissions summary
 */
export interface UserPermissionsSummary {
  userId: string
  role: BuiltInRole
  customRoles: CustomRoleSummary[]
  permissions: UserPermissionDetail[]
}

/**
 * Custom role summary
 */
export interface CustomRoleSummary {
  id: string
  name: string
  key: string
}

/**
 * Custom role detail
 */
export interface CustomRole {
  id: string
  tenantId: string
  key: string
  name: string
  description: string | null
  isSystemRole: boolean
  createdById: string | null
  createdAt: string
  updatedAt: string
  permissionCount?: number
  userCount?: number
}

/**
 * User permission override
 */
export interface UserPermissionOverride {
  id: string
  userId: string
  permissionId: string
  permissionKey: string
  permissionName: string
  granted: boolean
  expiresAt: string | null
  grantedById: string | null
  grantedByName: string | null
  reason: string | null
  createdAt: string
}

/**
 * User custom role assignment
 */
export interface UserCustomRoleAssignment {
  userId: string
  customRoleId: string
  customRoleName: string
  customRoleKey: string
  assignedById: string | null
  assignedByName: string | null
  assignedAt: string
  expiresAt: string | null
}

// Zod schemas for validation

export const permissionCategorySchema = z.enum([
  'transactions',
  'users',
  'reports',
  'imports',
  'settings',
  'billing',
  'audit',
  'data_retention',
  'teams',
  'admin',
])

export const builtInRoleSchema = z.enum(['super_admin', 'org_admin', 'user', 'viewer'])

export const permissionSchema = z.object({
  id: z.string().uuid(),
  key: z.string().regex(/^[a-z]+\.[a-z]+$/),
  name: z.string(),
  description: z.string().nullable(),
  category: permissionCategorySchema,
  isSystem: z.boolean(),
  createdAt: z.string().datetime(),
})

export const permissionSourceSchema = z.enum(['role', 'custom_role', 'team', 'override'])

export const userPermissionDetailSchema = z.object({
  key: z.string(),
  name: z.string(),
  category: permissionCategorySchema,
  granted: z.boolean(),
  source: permissionSourceSchema,
})

export const customRoleSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  key: z.string(),
})

export const userPermissionsSummarySchema = z.object({
  userId: z.string().uuid(),
  role: builtInRoleSchema,
  customRoles: z.array(customRoleSummarySchema),
  permissions: z.array(userPermissionDetailSchema),
})

export const customRoleSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  key: z.string().regex(/^[a-z_]+$/),
  name: z.string(),
  description: z.string().nullable(),
  isSystemRole: z.boolean(),
  createdById: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  permissionCount: z.number().optional(),
  userCount: z.number().optional(),
})

export const userPermissionOverrideSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  permissionId: z.string().uuid(),
  permissionKey: z.string(),
  permissionName: z.string(),
  granted: z.boolean(),
  expiresAt: z.string().datetime().nullable(),
  grantedById: z.string().uuid().nullable(),
  grantedByName: z.string().nullable(),
  reason: z.string().nullable(),
  createdAt: z.string().datetime(),
})

export const userCustomRoleAssignmentSchema = z.object({
  userId: z.string().uuid(),
  customRoleId: z.string().uuid(),
  customRoleName: z.string(),
  customRoleKey: z.string(),
  assignedById: z.string().uuid().nullable(),
  assignedByName: z.string().nullable(),
  assignedAt: z.string().datetime(),
  expiresAt: z.string().datetime().nullable(),
})

// Request/Response types

export type GetUserPermissionsResponse = {
  success: true
  data: UserPermissionsSummary
}

export type GetUserPermissionOverridesResponse = {
  success: true
  data: {
    overrides: UserPermissionOverride[]
  }
}

export type UpdateUserPermissionRequest = {
  permissionId: string
  granted: boolean
  expiresAt: string | null
  reason?: string
}

export type UpdateUserPermissionResponse = {
  success: true
  data: {
    override: UserPermissionOverride
  }
}

export type GetCustomRolesResponse = {
  success: true
  data: {
    roles: CustomRole[]
  }
}

export type CreateCustomRoleRequest = {
  key: string
  name: string
  description?: string
  permissions: string[]
}

export type CreateCustomRoleResponse = {
  success: true
  data: {
    role: CustomRole
  }
}

export type UpdateCustomRoleRequest = {
  name?: string
  description?: string
  permissions?: string[]
}

export type UpdateCustomRoleResponse = {
  success: true
  data: {
    role: CustomRole
  }
}

export type DeleteCustomRoleResponse = {
  success: true
}

export type AssignCustomRoleRequest = {
  customRoleId: string
  expiresAt?: string | null
}

export type AssignCustomRoleResponse = {
  success: true
}

export type RemoveCustomRoleResponse = {
  success: true
}

export type GetAllPermissionsResponse = {
  success: true
  data: {
    permissions: Permission[]
    categories: Array<{
      name: PermissionCategory
      label: string
      permissions: Permission[]
    }>
  }
}

// Zod-inferred types (use these for API validation)
export type PermissionSchema = z.infer<typeof permissionSchema>
export type UserPermissionDetailSchema = z.infer<typeof userPermissionDetailSchema>
export type CustomRoleSummarySchema = z.infer<typeof customRoleSummarySchema>
export type UserPermissionsSummarySchema = z.infer<typeof userPermissionsSummarySchema>
export type CustomRoleSchema = z.infer<typeof customRoleSchema>
export type UserPermissionOverrideSchema = z.infer<typeof userPermissionOverrideSchema>
export type UserCustomRoleAssignmentSchema = z.infer<typeof userCustomRoleAssignmentSchema>
